import React, { useCallback, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { EPaymentType, SetLoading, SetModalPayment } from '../../store/layout.slice';
import { StyleStripe } from '../../utils/modal';

import {loadStripe} from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';
import { AxiosClient } from '../../utils/axios';
import { EConvertType } from '../../@types/Convert';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHED_KEY || '');

function PaymentModal() {
    const dispatch = useAppDispatch();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const show = useAppSelector((state) => state.layout.modals.payment.show);
    const payment = useAppSelector((state) => state.layout.modals.payment);

    const [loadingText, setLoadingText] = useState<string>('');
    const [clientSecret, setClientSecret] = useState<string|null>(null);
    const [recaptchaToken, setRecaptchaToken] = useState<string>('');

    useEffect(() => {
        if(!show) return;
        verifyReCaptcha();
    }, [show]);

    useEffect(() => {
        if(!show || clientSecret || !recaptchaToken) return;

        setLoadingText(`Preparing for payment...`);
        switch(payment.type) {
            case EPaymentType.Translate: {
                AxiosClient.post(`/prepare-payment`, payment.data, { 
                    params: { action: 'create-translate-checkout-session' },
                    headers: {
                        'Recaptcha-Token': recaptchaToken
                    }
                }).then(({ data }) => {
                    setClientSecret(data.clientSecret);
                }).catch((err) => {
                    alert(err?.response?.data?.message || 'Something went wrong');
                    onCloseModal();
                }).finally(() => {
                    setLoadingText('');
                })

                break;
            }
            case EPaymentType.Convert: {
                var checkoutAction = null;

                switch(payment.convertType) {
                    case EConvertType.SrtToFCPXML: checkoutAction = 'create-srttofcpxml-convert-checkout-session'; break;
                    case EConvertType.XMLToFCPXML: checkoutAction = 'create-xmltofcpxml-convert-checkout-session'; break;
                    case EConvertType.SrtToAvidSubCap: checkoutAction = 'create-srttoavidsubcap-convert-checkout-session'; break;
                    case EConvertType.SrtToEDL: checkoutAction = 'create-srttoedl-convert-checkout-session'; break;
                    default: break;
                }

                AxiosClient.post(`/prepare-payment`, payment.data, { 
                    params: { action: checkoutAction },
                    headers: {
                        'Recaptcha-Token': recaptchaToken
                    }
                }).then(({ data }) => {
                    setClientSecret(data.clientSecret);
                }).catch((err) => {
                    alert(err?.response?.data?.message || 'Something went wrong');
                    onCloseModal();
                }).finally(() => {
                    setLoadingText('');
                })

                break;
            }
            default: 
                alert("Invalid payment type.");
                setLoadingText('');
                return;
        }
    }, [show, clientSecret, payment.data, payment.type, recaptchaToken]);

    const verifyReCaptcha = useCallback(async () => {
        if (!executeRecaptcha) return;
      
        setLoadingText('Verifying your identity...');
        let token = await executeRecaptcha('RequestPayment');

        setLoadingText('');
        setRecaptchaToken(token);
    }, [executeRecaptcha]);

    const onCloseModal = () => {
        dispatch(SetModalPayment({ show: false }));
    }

    const onClickClose = (e: any) => {
        e.preventDefault();
        onCloseModal();
    }

    return (
        <Modal isOpen={show} onRequestClose={onCloseModal} style={StyleStripe} contentLabel="Modal: Payment">
            <div className="px-0 py-6">
                <div className='px-6 flex justify-between items-center mb-2'>
                    <button onClick={onClickClose} className="btn btn-black"><i className="fa-solid fa-circle-arrow-left mr-1.5"></i>Back</button>
                </div>
                { loadingText && <div className='text-white py-16'>
                    <div className='h-full relative'>
                        <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'>
                            <div className="lds-roller"><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div></div>
                        </div>
                    </div>
                    <p className='text-gray-900 font-medium tracking-widest text-center mt-14'>{ loadingText }</p>
                </div>}
                { (!loadingText && clientSecret && recaptchaToken) && <div>
                    <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
                        <EmbeddedCheckout/>
                    </EmbeddedCheckoutProvider>
                </div> }
            </div>
        </Modal>
    )
}

export default React.memo(PaymentModal);